import React, { useEffect, useMemo, useState } from 'react';
import FormInput from '../../common/FormInput/FormInput';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { formatDate } from '../../../utils';
import { getDefaultPausedUntilDate, getScheduledResumeDate, getTodayBrtDateOnly } from '../../../utils/subscriptionPause';
import { PAYMENT_SYSTEM_LABELS, type GatewayProvider } from '../../../types/payment';
import { PAUSE_GATEWAY_DESCRIPTIONS } from './paymentDetails.constants';
import ModalSummary from './ModalSummary';
import styles from './CreateSubscriptionModal.module.css';

interface PauseSubscriptionModalProps {
  open: boolean;
  loading: boolean;
  provider: GatewayProvider;
  subscriptionPlanName: string | null;
  onConfirm: (params: { pausedUntil: string; reason: string | null }) => void;
  onCancel: () => void;
}

const PauseSubscriptionModal: React.FC<PauseSubscriptionModalProps> = ({
  open,
  loading,
  provider,
  subscriptionPlanName,
  onConfirm,
  onCancel,
}) => {
  const [pausedUntil, setPausedUntil] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(getTodayBrtDateOnly, [open]);
  const minPausedUntil = today;

  useEffect(() => {
    if (!open) {
      setPausedUntil('');
      setReason('');
      setError(null);
      return;
    }

    setPausedUntil(getDefaultPausedUntilDate());
    setReason('');
    setError(null);
  }, [open]);

  useEscapeToClose(onCancel, open);

  if (!open) return null;

  const scheduledResumeDate = pausedUntil
    ? getScheduledResumeDate(pausedUntil)
    : null;

  const handleConfirm = () => {
    if (!pausedUntil) {
      setError('Informe a data até a qual a assinatura ficará pausada.');
      return;
    }
    if (pausedUntil < today) {
      setError('A data de retorno não pode ser anterior a hoje.');
      return;
    }
    onConfirm({ pausedUntil, reason: reason.trim() ? reason.trim() : null });
  };

  const gatewayLabel = PAYMENT_SYSTEM_LABELS[provider] ?? provider;

  return (
    <div className={styles.overlay}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        style={{ width: 520, alignItems: 'stretch' }}
      >
        <div className={styles['icon-circle']} style={{ alignSelf: 'center' }}>
          <span className="material-symbols-outlined">pause_circle</span>
        </div>

        <h3 className={styles.title}>Pausar assinatura</h3>
        <p className={styles.message}>
          A aluna ficará marcada como pausada
          {subscriptionPlanName ? ` no plano "${subscriptionPlanName}"` : ''} até a data selecionada.
          A linha do tempo da assinatura é preservada: o período pausado não consome tempo ativo e a
          próxima cobrança é ajustada automaticamente.
        </p>

        <FormInput
          id="pause-subscription-until"
          label="Pausar até"
          type="date"
          required
          value={pausedUntil}
          onChange={(event) => {
            setPausedUntil(event.target.value);
            if (error) setError(null);
          }}
          min={minPausedUntil}
          disabled={loading}
        />

        <FormInput
          id="pause-subscription-reason"
          label="Motivo (opcional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Ex.: cirurgia, viagem, licença médica"
          disabled={loading}
        />

        <ModalSummary
          items={[
            { label: 'Gateway', value: gatewayLabel },
            {
              label: 'Retorno automático',
              value: scheduledResumeDate ? formatDate(scheduledResumeDate) : '—',
            },
          ]}
        />

        <p className={styles.validation} style={{ textAlign: 'left' }}>
          {PAUSE_GATEWAY_DESCRIPTIONS[provider]}
        </p>

        <p className={styles.message} style={{ textAlign: 'left' }}>
          Você poderá retomar a assinatura antes do prazo pelo botão “Retomar assinatura”. O histórico
          de pagamentos, treinos, anamnese e demais dados da aluna permanecem intactos.
        </p>

        {error && <p className={styles.validation}>{error}</p>}

        <div className={styles.actions}>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-cancel']}`}
            onClick={onCancel}
            disabled={loading}
          >
            Voltar
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles['btn-confirm']}`}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Pausando…' : 'Pausar assinatura'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PauseSubscriptionModal;
